import { Header } from '../Header';
import { Footer } from '../Footer/Footer';
import { EditorState } from '../../state';
import { Accordion } from '../Accordion';
import { HelpModal } from '../HelpModal';
import { FormSidebar } from '../FormSidebar';
import { AppDispatch } from '../../../schematic/reducer';
import { Schematic } from '../../../schematic/Schematic';
import { RemoveSystemModal } from '../RemoveSystemModal';
import { DeleteSchematicModal } from '../DeleteSchematicModal';
import {deleteSchematic, enterFullscreen, leaveFullscreen} from '../../actions';
import React, {ChangeEvent, useCallback, useEffect, useRef, useState} from 'react';
import { loadSchematic } from '../../../schematic/actions';
import { AppModel } from '../../../schematic/types';
import {StyledDumbledoreEditor} from "./DumbledoreEditorStyled";
import {ErrorMessageModal} from "../ErrorMessageModal";


export interface DumbledoreEditorProps {
  getState: () => EditorState;
  dispatch: AppDispatch;
  exportModel: () => AppModel;
}

export const DumbledoreEditor: React.FC<DumbledoreEditorProps> = ({ getState, dispatch, exportModel }) => {
  const editorElement = useRef<HTMLDivElement>(null);
  const editorState = getState();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const onFullscreenChangeDomEvent = useCallback(() => {
    if (document.fullscreenElement) {
      dispatch(enterFullscreen());
    } else {
      dispatch(leaveFullscreen());
    }
  }, []);

  useEffect(() => {
    document.addEventListener('fullscreenchange', onFullscreenChangeDomEvent);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChangeDomEvent);
  });

  const onFullscreen = useCallback(() => {
    if (editorElement.current) {
      editorElement.current.requestFullscreen();
    }
  }, [editorElement.current]);

  const selectedSystemId = editorState.schematic.selectedSystemId;
  const selectedSystem = selectedSystemId ? editorState.schematic.systems.find((s) => s.hasId === selectedSystemId) : null;
  const getSchematicState = useCallback(() => editorState.schematic, [editorState]);

  const fileUploadChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      resetFileName();
      return;
    }

    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = function (evt) {
      if (evt?.target?.readyState !== FileReader.DONE) {
        setIsError(true);
        setErrorMessage('File reading operation not completed');
        return;
      }

      if (evt.target.error) {
        setIsError(true);
        setErrorMessage('Error occurred while reading the file');
        return;
      }

      const fileContent = evt.target.result;
      try {
        const appModel = JSON.parse(fileContent as string);
        dispatch(loadSchematic({ schematic: appModel }));
      } catch (error) {
        setIsError(true);
        setErrorMessage(`<p>Error occurred while parsing the JSON data.</p>${error}`);
      }
    };

    reader.onerror = function () {
      alert('An error occurred while reading the file');
      setIsError(true);
      setErrorMessage('An error occurred while reading the file');
    };

    reader.readAsText(file);
    resetFileName();
  };

  const resetFileName = () => {
    if (inputRef.current) {
      inputRef.current.value = ''; // Reset the value of the input element
    }
  }

  const clearSchematics = () => {
    resetFileName();
    dispatch(deleteSchematic());
  };

  const exportSchematics = () => {
    const exportData = exportModel();
    const blob = new Blob(["\ufeff", JSON.stringify(exportData)], { type: 'application/json' });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', 'schematics.json');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <StyledDumbledoreEditor>
      <div className="editor" ref={editorElement}>
        {editorState.showingConfirmDeleteModal && <DeleteSchematicModal dispatch={dispatch} />}
        {editorState.showingRemoveSystemModal && <RemoveSystemModal dispatch={dispatch} systemId={selectedSystemId} />}
        {editorState.showingHelp && <HelpModal dispatch={dispatch} />}
        {isError && <ErrorMessageModal isError={isError} message={errorMessage} setIsError={setIsError} />}
        <Header
          isFullscreen={editorState.isFullscreen}
          onFullscreen={onFullscreen}
          dispatch={dispatch}
          selectedSystemId={selectedSystemId} />
        <main className="main">
          <Accordion />
          <div className="schematic-container">
            <Schematic getState={getSchematicState} dispatch={dispatch} />
          </div>
          <FormSidebar
            buildingInfo={editorState.buildingInfo}
            tabState={editorState.tabState}
            systemState={selectedSystem}
            dispatch={dispatch} />
        </main>
        { editorState.showDevTools &&
          <Footer dispatch={ dispatch } selectedSystemId={ selectedSystemId } getState={ getState }>
            <button className="button" onClick={ clearSchematics }>Clear</button>
            <label htmlFor="fileUpload" className="fileUploadButton">Import</label>
            <input
              id="fileUpload"
              type="file"
              onChange={fileUploadChange}
              key={inputRef.current?.value}
              hidden
              ref={inputRef}
            />
            <button className="button" onClick={ exportSchematics }>Export</button>
          </Footer>
        }
      </div>
    </StyledDumbledoreEditor>
  );
};


